import React, { useMemo } from 'react';

const rangeOptions = [
	{ label: '7 ngày', value: '7days' },
	{ label: '30 ngày', value: '30days' }
];

const chartWidth = 900;
const chartHeight = 260;
const paddingX = 56;
const paddingY = 28;

const toDateKey = (value) => {
	if (!value) return '';
	const raw = String(value);
	const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
	if (iso) return iso[1];
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
};

const formatLabel = (item) => {
	const dateValue = typeof item === 'object' && item !== null ? item.date : item;
	const timeValue = typeof item === 'object' && item !== null ? item.time : '';
	const key = toDateKey(dateValue);
	if (!key) return '';
	const [, , month, day] = key.match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
	const datePart = day && month ? `${day}/${month}` : key;
	return timeValue ? `${datePart} ${timeValue}` : datePart;
};

const formatValue = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '—';
	return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
};

const getPlotWidth = (width) => width - paddingX * 2;

const getXPosition = (index, count, width) => {
	const plotWidth = getPlotWidth(width);
	if (count <= 1) return paddingX + plotWidth / 2;
	return paddingX + (plotWidth / (count - 1)) * index;
};

const computeYBounds = (values) => {
	if (!values.length) return { min: 0, max: 100 };
	const min = Math.min(...values);
	const max = Math.max(...values);
	if (min === max) {
		const delta = Math.max(Math.abs(min) * 0.1, 1);
		return { min: min - delta, max: max + delta };
	}
	const spread = max - min;
	const padding = spread * 0.08;
	return { min: min - padding, max: max + padding };
};

const buildSeriesPoints = (series, key, width, height, yMin, yMax) => {
	const plotHeight = height - paddingY * 2;
	const spread = yMax - yMin || 1;

	return series
		.map((item, index) => {
			const numericValue = Number(item?.[key]);
			if (!Number.isFinite(numericValue)) return null;

			const x = getXPosition(index, series.length, width);
			const y = height - paddingY - ((numericValue - yMin) / spread) * plotHeight;
			return { x, y, value: numericValue, index };
		})
		.filter(Boolean);
};

const pointsToPath = (points) => {
	if (!points.length) return '';
	return points
		.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
		.join(' ');
};

const sortSeries = (series) => {
	return [...series].sort((left, right) => {
		const leftKey = `${toDateKey(left?.date)} ${left?.time || '00:00'}`;
		const rightKey = `${toDateKey(right?.date)} ${right?.time || '00:00'}`;
		return leftKey.localeCompare(rightKey);
	});
};

const seriesItemKey = (item, index) => `${toDateKey(item?.date)}-${item?.time || '00:00'}-${index}`;

const LineChartCard = ({ title, subtitle, series, lines, yLabel }) => {
	const sortedSeries = useMemo(() => sortSeries(series), [series]);

	const sharedBounds = useMemo(() => {
		const values = lines
			.flatMap((line) => sortedSeries.map((item) => Number(item?.[line.key])))
			.filter((value) => Number.isFinite(value));
		return computeYBounds(values);
	}, [lines, sortedSeries]);

	const gridLines = 4;
	const showTrendHint = sortedSeries.length === 1;

	return (
		<div className="health-chart-card">
			<div className="health-chart-header">
				<div>
					<h3>{title}</h3>
					<p>{subtitle}</p>
					{showTrendHint && (
						<p className="health-chart-hint">Chỉ có 1 lần đo — nhập thêm ngày khác để thấy xu hướng.</p>
					)}
				</div>
				<span className="health-chart-axis">{yLabel}</span>
			</div>

			{sortedSeries.length === 0 ? (
				<div className="health-empty-chart">Chưa có dữ liệu để hiển thị biểu đồ.</div>
			) : (
				<>
					<div className="health-chart-legend">
						{lines.map((line) => (
							<span key={line.key} className="health-legend-item">
								<i style={{ background: line.color }} />
								{line.label}
							</span>
						))}
					</div>

					<div className="health-svg-wrap">
						<svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label={title}>
							{Array.from({ length: gridLines + 1 }).map((_, index) => {
								const y = paddingY + ((chartHeight - paddingY * 2) / gridLines) * index;
								const value =
									sharedBounds.max - ((sharedBounds.max - sharedBounds.min) / gridLines) * index;
								return (
									<g key={index}>
										<line
											x1={paddingX}
											x2={chartWidth - paddingX}
											y1={y}
											y2={y}
											className="health-grid-line"
										/>
										<text x={10} y={y + 4} className="health-grid-label">
											{formatValue(value)}
										</text>
									</g>
								);
							})}

							{lines.map((line) => {
								const points = buildSeriesPoints(
									sortedSeries,
									line.key,
									chartWidth,
									chartHeight,
									sharedBounds.min,
									sharedBounds.max
								);

								return (
									<g key={line.key}>
										{points.length > 1 && (
											<path
												d={pointsToPath(points)}
												className="health-line"
												stroke={line.color}
												fill="none"
											/>
										)}
										{points.map((point) => (
											<g key={`${line.key}-${point.index}`}>
												<circle
													cx={point.x}
													cy={point.y}
													r={5}
													fill={line.color}
													stroke="#ffffff"
													strokeWidth={2}
												>
													<title>
														{line.label}: {formatValue(point.value)}
													</title>
												</circle>
												<text
													x={point.x + 8}
													y={point.y - 8}
													className="health-point-label"
													fill={line.color}
												>
													{formatValue(point.value)}
												</text>
											</g>
										))}
									</g>
								);
							})}

							{sortedSeries.map((item, index) => {
								const x = getXPosition(index, sortedSeries.length, chartWidth);
								return (
									<g key={seriesItemKey(item, index)}>
										<line
											x1={x}
											x2={x}
											y1={paddingY}
											y2={chartHeight - paddingY}
											className="health-x-line"
										/>
										<text x={x} y={chartHeight - 6} textAnchor="middle" className="health-date-label">
											{formatLabel(item)}
										</text>
									</g>
								);
							})}
						</svg>
					</div>
				</>
			)}
		</div>
	);
};

const HealthCharts = ({ series = [], range = '7days', onRangeChange }) => {
	const bpLines = [
		{ key: 'systolic', label: 'Huyết áp trên', color: '#ef4444' },
		{ key: 'diastolic', label: 'Huyết áp dưới', color: '#f59e0b' },
		{ key: 'heartRate', label: 'Nhịp tim', color: '#2563eb' }
	];

	const bloodSugarLine = [{ key: 'bloodSugar', label: 'Đường huyết', color: '#16a34a' }];
	const weightLine = [{ key: 'weight', label: 'Cân nặng', color: '#7c3aed' }];

	return (
		<section className="health-card health-chart-shell">
			<div className="health-card-header">
				<div>
					<p className="health-eyebrow">Tình trạng sức khỏe tổng hợp</p>
					<h2>Biểu đồ xu hướng</h2>
				</div>
				<div className="health-range-switcher">
					{rangeOptions.map((option) => (
						<button
							key={option.value}
							type="button"
							className={`health-range-btn ${range === option.value ? 'active' : ''}`}
							onClick={() => onRangeChange?.(option.value)}
						>
							{option.label}
						</button>
					))}
				</div>
			</div>

			<div className="health-chart-grid">
				<LineChartCard
					title="Huyết áp & Nhịp tim"
					subtitle="Theo dõi huyết áp tâm thu, tâm trương và nhịp tim theo ngày"
					series={series}
					lines={bpLines}
					yLabel="mmHg / bpm"
				/>

				<LineChartCard
					title="Đường huyết"
					subtitle="Theo dõi đường huyết (mmol/L) theo từng lần đo"
					series={series}
					lines={bloodSugarLine}
					yLabel="mmol/L"
				/>

				<LineChartCard
					title="Cân nặng"
					subtitle="Theo dõi cân nặng (kg) theo từng lần đo"
					series={series}
					lines={weightLine}
					yLabel="kg"
				/>
			</div>
		</section>
	);
};

export default HealthCharts;
